import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '../lib/SignaSessionContext';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SkeletonList } from '../components/ui/Skeleton';

// Vuelta del autorizador de Signa (`/auth/callback`). Canjea el código por tokens y sigue viaje.
//
// El canje corre UNA sola vez: un `authorization_code` es de un solo uso, y en React 19 con
// StrictMode los efectos se montan dos veces en desarrollo — sin este candado, el segundo intento
// falla con `invalid_grant` y la persona ve un error en un login que en realidad salió bien.

export function CallbackPage() {
  const { completeLogin } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    completeLogin(location.search)
      .then((returnTo) => navigate(returnTo, { replace: true }))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'No pudimos completar el ingreso.'));
  }, [completeLogin, location.search, navigate]);

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <Card padding="lg" className="w-full max-w-md">
        {error ? (
          <div role="alert">
            <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">No pudimos completar el ingreso</h1>
            {/* El mensaje ya viene mapeado a español desde `signaAuth`; nunca el texto crudo del IdP. */}
            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{error}</p>
            <div className="mt-8">
              <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/login', { replace: true })}>
                Volver a intentar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">Entrando…</h1>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Estamos verificando tu sesión con Signa.</p>
            <div className="mt-6">
              <SkeletonList rows={2} label="Verificando tu sesión con Signa" />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export default CallbackPage;
